import { useLayoutEffect, useRef, useState } from 'react';
import { genUuid } from '@/lib/uuid';
import { useCuttingStore } from './state/useCuttingStore';
import { CuttingModal2 } from './dialogs/CuttingModal2';
import { EdgeModal } from './dialogs/EdgeModal';
import { GrooveModal } from './dialogs/GrooveModal';
import { ProcessModal } from './dialogs/ProcessModal';
import { ImportFormatDialog } from './dialogs/ImportFormatDialog';
import { countParts, makeEmptyPart, partSizeStatus } from './model/partsUtils';
import { nextCell, type CellDir } from './keyboard/cellNav';
import { computePartsSummary } from './lib/cuttingMetrics';
import { NumericKeyboard } from './keyboard/NumericKeyboard';
import { useCoarsePointer } from './keyboard/useCoarsePointer';
import { usePinchZoom } from './gestures/usePinchZoom';
import { useMediaQuery } from '@/hooks/useMediaQuery';
import type { EdgeAssignment, GrooveAssignment } from './model/types';
import { EdgeMiniSvg, GrooveMiniSvg } from './components/EdgeGrooveMiniSvg';

/** Числовые поля детали, привязанные к цифровой клавиатуре (.kb-input). */
type NumField = 'length' | 'width' | 'qty';

/**
 * Block 14 «Раскрой» — вкладка «Детали и кромки» (PR #6a-PARTS, CUT-2.3/2.5/2.6).
 *
 * Пиксельный порт `project.html:186-377` (триггер размера листа + тулбар 7 кнопок +
 * таблица 10 колонок + футер). Связка со стором (in-memory, PR #1/#4): parts-CRUD →
 * dirty=true → автосейв PR#4 подхватывает.
 *
 * Графический селектор кромок = PR #10a (CUT-3.3): клик по ячейке «Кромки» →
 * `EdgeModal` → ОК пишет `part.edges` → `EdgeMiniSvg` перерисовывается из стора.
 * Пазы (cell-groove) = PR #10b-1 → `GrooveModal`. Обработка (cell-process) = PR #10b-2 →
 * `ProcessModal` (список-словарь, клик применяет сразу `part.processing`; chip = текст
 * обработки или «Нет»). Toolbar-кнопка «⬚ Кромка» (multi-apply) — заглушка (B-230).
 * Кнопка «✂ Раскроить» (PR #21, CUT-5.1): запускает движок раскроя КЛИЕНТСКИ
 * (`runCutting` стора, ADR-0079) и через `onCutComplete` переключает на вкладку
 * «Результат»; блокируется при детали > листа (§5.1.10, валидация красит строку +
 * disable «Раскроить»). Текст/кнопки — тёплый `--text-primary` #1a1512
 * (вербатим живого прототипа, Owner Preview-сверка); кнопки нейтральные.
 *
 * PR #8a-MOBILE (правка #7): триггер «Размер листа» ПЕРЕНЕСЁН отсюда на вкладку
 * «Настройки» (ParamsTab, первым блоком). Здесь `sheetConfig`/`edgeOffset` остаются
 * только для валидации «деталь > листа» (§5.1.10) — UI-триггера и модалки тут нет.
 */

const EMPTY_EDGES: EdgeAssignment = { top: '', bottom: '', left: '', right: '' };
const EMPTY_GROOVES: GrooveAssignment = { top: '', right: '', bottom: '', left: '' };

// EdgeMiniSvg/GrooveMiniSvg вынесены в общий модуль (refine #168-A) — переиспользуются
// в печати (CutPrintDetails). См. components/EdgeGrooveMiniSvg.tsx (verbatim).

interface PartsTabProps {
  /** Колбэк после запуска раскроя — родитель переключает на вкладку «Результат». */
  onCutComplete?: () => void;
}

export function PartsTab({ onCutComplete }: PartsTabProps = {}) {
  const parts = useCuttingStore((s) => s.document.parts);
  const sheetConfig = useCuttingStore((s) => s.document.settings.sheetConfig);
  const edgeOffset = useCuttingStore((s) => s.document.settings.edgeOffset);
  const minPartSize = useCuttingStore((s) => s.document.settings.minPartSize);
  const addPart = useCuttingStore((s) => s.addPart);
  const updatePart = useCuttingStore((s) => s.updatePart);
  const removePart = useCuttingStore((s) => s.removePart);
  const duplicatePart = useCuttingStore((s) => s.duplicatePart);
  const clearParts = useCuttingStore((s) => s.clearParts);
  const mergeIdenticalParts = useCuttingStore((s) => s.mergeIdenticalParts);
  const autosaveDraft = useCuttingStore((s) => s.autosaveDraft);
  const runCutting = useCuttingStore((s) => s.runCutting);

  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [focusId, setFocusId] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'import' | 'clear' | null>(null);
  // PR #10a-EDGE (CUT-3.3): графический селектор кромок. Открытая ячейка = {id,label}.
  const [edgeModal, setEdgeModal] = useState<{ id: string; label: number } | null>(null);
  // PR #10b-1-GROOVE (CUT-3.3): графический селектор пазов. Открытая ячейка = {id,label}.
  const [grooveModal, setGrooveModal] = useState<{ id: string; label: number } | null>(null);
  // PR #10b-2 (CUT-3.3): селектор обработки (список-словарь). Открытая ячейка = {id,label}.
  const [processModal, setProcessModal] = useState<{ id: string; label: number } | null>(null);

  // CUT-2.4: цифровая клавиатура. PR #8b итерация 3 (решение владельца 10.06.2026):
  // #7-FIX скорректирован — экранная клавиатура ТОЛЬКО на touch (coarse pointer);
  // десктоп (fine pointer) использует физическую клавиатуру, экранная не всплывает.
  const [activeCell, setActiveCell] = useState<{ id: string; field: NumField } | null>(null);
  const coarse = useCoarsePointer();

  // PR #8b (ADR-0071, реализует ADR-0069 D4): Excel-модель таблицы ТОЛЬКО на
  // мобильном (≤480px). Freeze-header (нативный sticky thead) + нативный 2D-скролл
  // (.sc-table-wrap overflow:auto) + pinch-zoom через размер ячеек (--cell-scale).
  // usePinchZoom добавляет только 2-пальцевый зум; pan/tap — нативные. TabBar/
  // клавиатура/тулбар/кнопки действий — структурно ВНЕ .sc-table-wrap (фиксированы).
  const tableWrapRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const isMobile = useMediaQuery('(max-width: 480px)');
  usePinchZoom({ viewportRef: tableWrapRef, contentRef: tableRef, enabled: isMobile });

  // 2c (фантомный ряд): стор `parts` стартует пустым (нет 0×0-мусора в персисте/
  // черновиках, футер «Деталей» честный). Пока деталей нет — рендерим ОДИН пустой
  // редактируемый ряд (sentinel) со СТАБИЛЬНЫМ заранее сгенерированным id, чтобы при
  // первой правке (`ensureRow` → addPart с этим id) React переиспользовал тот же
  // DOM-узел (key совпадает) и НЕ терял фокус/каретку.
  const phantomIdRef = useRef<string>(genUuid());
  const isPhantom = parts.length === 0;
  const rows: typeof parts = isPhantom ? [{ ...makeEmptyPart(), id: phantomIdRef.current }] : parts;

  /** Материализовать фантом при первой правке поля: вставить реальную деталь с тем
      же стабильным id и вернуть его. Для уже реальной строки — id без изменений. */
  const ensureRow = (id: string): string => {
    if (isPhantom && id === phantomIdRef.current) {
      addPart({ id });
      phantomIdRef.current = genUuid(); // следующий фантом получит свежий id
      return id;
    }
    return id;
  };

  // 2a desktop-навигация: фокус целевого инпута по data-атрибутам, СКОУПЛЕННО своей
  // таблицей (`tableRef`) — без коллизий с одинаковыми aria-label в OffcutsTab.
  const pendingFocus = useRef<{ id: string; field: NumField } | null>(null);
  const focusInput = (rowId: string, field: NumField) => {
    const el = tableRef.current?.querySelector<HTMLInputElement>(
      `[data-row-id="${CSS.escape(rowId)}"][data-field="${field}"]`,
    );
    el?.focus();
    el?.select();
  };
  // Новая строка (↓ за конец) появляется в DOM лишь после ре-рендера — фокусируем её
  // в layout-эффекте по отложенной цели.
  useLayoutEffect(() => {
    const pf = pendingFocus.current;
    if (pf) {
      pendingFocus.current = null;
      focusInput(pf.id, pf.field);
    }
  });

  const allChecked = parts.length > 0 && parts.every((p) => checked.has(p.id));

  const handleAdd = () => {
    const p = addPart();
    setSelectedId(p.id);
    setFocusId(p.id);
  };

  const handleDuplicate = () => {
    if (selectedId) duplicatePart(selectedId);
  };

  const handleRemove = () => {
    if (selectedId) {
      removePart(selectedId);
      setSelectedId(null);
    }
  };

  const toggleAll = (on: boolean) => {
    setChecked(on ? new Set(parts.map((p) => p.id)) : new Set());
  };

  const toggleRow = (id: string, on: boolean) => {
    setChecked((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  };

  const toNum = (v: string): number => {
    const n = parseInt(v, 10);
    return Number.isNaN(n) ? 0 : Math.max(0, n); // отрицательные → 0 (неполная деталь)
  };

  /** Записать числовое поле детали. 2b: qty допускает 0 во время ввода (пустое поле
      показывается как '', как длина/ширина) — нормализуется в 1 на blur/закрытии
      клавиатуры (`normalizeQty`). Длина/ширина ≥ 0. Общий путь для инпутов и
      цифровой клавиатуры (без расхождения логики). */
  const setField = (id: string, field: NumField, raw: string) => {
    if (field === 'qty') {
      const v = parseInt(raw, 10);
      updatePart(id, { qty: Number.isNaN(v) ? 0 : Math.max(0, v) });
    } else {
      updatePart(id, { [field]: toNum(raw) });
    }
  };

  /** Правка поля из UI: сперва материализуем фантом (2c), затем пишем в стор.
      `set` синхронен (zustand) — `setField` уже видит вставленную деталь. */
  const commitField = (id: string, field: NumField, raw: string) => {
    setField(ensureRow(id), field, raw);
  };

  /** 2b: вернуть пустое qty к дефолту 1 при выходе из поля (blur / закрытие
      клавиатуры). На фантоме (id не в сторе) — no-op. */
  const normalizeQty = (id: string) => {
    const p = parts.find((x) => x.id === id);
    if (p && p.qty < 1) updatePart(id, { qty: 1 });
  };

  // ── Цифровая клавиатура (CUT-2.4) ──────────────────────────────────────
  const KB_FIELDS: NumField[] = ['length', 'width', 'qty'];

  /** Строка активного поля для клавиатуры (пустые длина/ширина/кол-во = '', не '0'). */
  const fieldString = (id: string, field: NumField): string => {
    const p = parts.find((x) => x.id === id);
    if (!p) return '';
    const v = p[field];
    return v > 0 ? String(v) : '';
  };

  const focusCell = (id: string, field: NumField) => {
    setActiveCell({ id, field });
  };

  /** Активна ли ячейка (для явной подсветки `.cell-active` — работает и на клик, и на
      программную стрелочную навигацию, в отличие от хрупкого CSS `:focus`). */
  const isCellActive = (id: string, field: NumField) =>
    activeCell?.id === id && activeCell.field === field;

  /** Снять подсветку при уходе фокуса ВНЕ таблицы (desktop). На touch `activeCell`
      управляет экранной клавиатурой — чистится через её `onClose`, не здесь. */
  const handleTableBlur = (e: React.FocusEvent<HTMLTableElement>) => {
    if (coarse) return;
    if (!e.currentTarget.contains(e.relatedTarget as Node | null)) setActiveCell(null);
  };

  /** Навигация экранной клавиатуры (touch): двигает `activeCell` по `rows` (учитывает
      фантом). Геометрия — общая `nextCell`. 2b: при уходе из qty нормализуем пустое в 1. */
  const handleKbNavigate = (dir: CellDir) => {
    if (!activeCell) return;
    if (activeCell.field === 'qty') normalizeQty(activeCell.id);
    const rowIdx = rows.findIndex((r) => r.id === activeCell.id);
    const colIdx = KB_FIELDS.indexOf(activeCell.field);
    if (rowIdx === -1 || colIdx === -1) return;
    const nx = nextCell(rowIdx, colIdx, dir, KB_FIELDS.length, rows.length);
    if (nx.kind === 'none') return;
    const field = KB_FIELDS[nx.col] ?? 'length';
    if (nx.kind === 'append') {
      if (isPhantom) return; // ↓ на пустом фантоме не плодит 0×0-деталь (цель 2c)
      const np = addPart();
      setActiveCell({ id: np.id, field });
      return;
    }
    const target = rows[nx.row];
    if (target) setActiveCell({ id: target.id, field });
  };

  /** 2a: навигация по ячейкам физической клавиатурой (desktop, Excel-стиль).
      ↑/↓ — та же колонка соседний ряд; ←/→ — соседняя ячейка ВСЕГДА (независимо от
      позиции каретки — решение владельца 15.06.2026: курсор внутри числа стрелками
      двигать не нужно). ↓ за последний ряд → новая деталь + фокус. Геометрия —
      общая `nextCell`, фокус — `focusInput`. Подсветку активной ячейки гоним из
      состояния `activeCell` (класс `.cell-active`) — ставим её ЯВНО здесь, не
      полагаясь на тайминг `onFocus`, чтобы жёлтая подсветка следовала за стрелками. */
  const handleCellKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    rowId: string,
    field: NumField,
  ) => {
    const dirMap: Record<string, CellDir> = {
      ArrowUp: 'up',
      ArrowDown: 'down',
      ArrowLeft: 'left',
      ArrowRight: 'right',
    };
    const dir = dirMap[e.key];
    if (!dir) return;
    const rowIdx = rows.findIndex((r) => r.id === rowId);
    const colIdx = KB_FIELDS.indexOf(field);
    if (rowIdx === -1 || colIdx === -1) return;
    const nx = nextCell(rowIdx, colIdx, dir, KB_FIELDS.length, rows.length);
    if (nx.kind === 'none') {
      e.preventDefault();
      return;
    }
    e.preventDefault();
    if (field === 'qty') normalizeQty(rowId); // 2b: пустое qty → 1 при уходе
    const targetField = KB_FIELDS[nx.col] ?? 'length';
    if (nx.kind === 'append') {
      if (isPhantom) return; // ↓ на пустом фантоме не плодит 0×0-деталь (цель 2c)
      const np = addPart();
      setActiveCell({ id: np.id, field: targetField }); // подсветка новой строки
      pendingFocus.current = { id: np.id, field: targetField };
      return;
    }
    const target = rows[nx.row];
    if (target) {
      setActiveCell({ id: target.id, field: targetField }); // подсветка идёт за стрелками
      focusInput(target.id, targetField);
    }
  };

  // ADR-0087: «Раскроить» заблокирована, пока есть детали недопустимого размера
  // (слишком маленькие < minPartSize ИЛИ больше листа − 2×обпил).
  const hasInvalid = parts.some(
    (p) => partSizeStatus(p, sheetConfig, edgeOffset, minPartSize) !== 'ok',
  );

  // CUT-3.2 (PR #12): метрики ДЕТАЛЕЙ для инфо-панели футера (площадь/кромки/пазы).
  // Реактивно из parts — пересчёт на каждое изменение размеров/кромок/пазов/qty.
  const summary = computePartsSummary(parts);

  return (
    <div>
      {/* Тулбар (project.html:198-207, 7 кнопок вербатим). `data-tb` — стабильные
          якоря для мобильной сетки 2/3/2 (правка #8, CSS @media ≤480px order+span);
          на десктопе — один ряд (flex), data-tb визуально нейтральны. */}
      <div className="sc-toolbar">
        <button type="button" data-tb="add" className="btn btn-success btn-sm" onClick={handleAdd}>
          + Добавить деталь
        </button>
        <button
          type="button"
          data-tb="dup"
          className="btn btn-ghost btn-sm"
          title="Дублировать выделенную строку"
          onClick={handleDuplicate}
        >
          ⧉ Дублировать
        </button>
        <button
          type="button"
          data-tb="del"
          className="btn btn-danger btn-sm"
          onClick={handleRemove}
        >
          × Удалить деталь
        </button>
        <button
          type="button"
          data-tb="clear"
          className="btn btn-danger btn-sm"
          title="Удалить все детали"
          onClick={() => setDialog('clear')}
        >
          ⊘ Очистить всё
        </button>
        <button
          type="button"
          data-tb="import"
          className="btn btn-secondary btn-sm"
          onClick={() => setDialog('import')}
        >
          📥 Импорт
        </button>
        <button
          type="button"
          data-tb="merge"
          className="btn btn-ghost btn-sm"
          onClick={() => mergeIdenticalParts()}
        >
          ⤴ Объединить одинаковые
        </button>
        <button
          type="button"
          data-tb="edge"
          className="btn btn-ghost btn-sm"
          title="Массовое назначение кромки выбранным деталям — в следующей версии"
          onClick={() => {
            /* Multi-apply кромки ко всем выбранным (прототип _edgeMultiTargets,
               app.js:311-321) — отложено за рамки PR #10a (B-230). Per-деталь
               селектор работает кликом по ячейке «Кромки». */
          }}
        >
          ⬚ Кромка
        </button>
      </div>

      {/* Таблица (project.html:209-363). PR #8b: .sc-table-wrap = нативный
          2D-скролл-вьюпорт (freeze-header через sticky thead); .sc-table несёт
          --cell-scale (pinch-zoom размером ячеек). */}
      <div className="sc-table-wrap" ref={tableWrapRef}>
        <table className="sc-table" ref={tableRef} onBlur={handleTableBlur}>
          <thead>
            <tr>
              <th style={{ width: 30 }}>№</th>
              <th>Длина</th>
              <th>Ширина</th>
              <th>Кол-во</th>
              <th>Кромки</th>
              <th style={{ width: 50 }}>
                Выбрать
                <br />
                <input
                  type="checkbox"
                  aria-label="Выбрать все детали"
                  checked={allChecked}
                  onChange={(e) => toggleAll(e.target.checked)}
                />
              </th>
              <th>Наименование</th>
              <th title="Можно ли поворачивать деталь на 90°">Вращать</th>
              <th>Пазы</th>
              <th>Обработка</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((part, idx) => {
              const n = idx + 1;
              const invalid = partSizeStatus(part, sheetConfig, edgeOffset, minPartSize) !== 'ok';
              const rowClass = [
                selectedId === part.id ? 'row-selected' : '',
                invalid ? 'row-error' : '',
              ]
                .filter(Boolean)
                .join(' ');
              const rotateYes = part.rotation === 'allow';
              return (
                <tr
                  key={part.id}
                  className={rowClass || undefined}
                  title={invalid ? 'Недопустимый размер' : undefined}
                  onClick={() => setSelectedId(part.id)}
                >
                  <td className="sc-row-counter">{n}</td>
                  <td className="cell-num">
                    <input
                      type="text"
                      inputMode={coarse ? 'none' : 'numeric'}
                      className={`cell-input kb-input${isCellActive(part.id, 'length') ? ' cell-active' : ''}`}
                      aria-label={`Длина ${n}`}
                      data-row-id={part.id}
                      data-field="length"
                      value={part.length > 0 ? part.length : ''}
                      autoFocus={focusId === part.id}
                      onFocus={() => focusCell(part.id, 'length')}
                      onKeyDown={(e) => handleCellKeyDown(e, part.id, 'length')}
                      onChange={(e) => commitField(part.id, 'length', e.target.value)}
                    />
                  </td>
                  <td className="cell-num">
                    <input
                      type="text"
                      inputMode={coarse ? 'none' : 'numeric'}
                      className={`cell-input kb-input${isCellActive(part.id, 'width') ? ' cell-active' : ''}`}
                      aria-label={`Ширина ${n}`}
                      data-row-id={part.id}
                      data-field="width"
                      value={part.width > 0 ? part.width : ''}
                      onFocus={() => focusCell(part.id, 'width')}
                      onKeyDown={(e) => handleCellKeyDown(e, part.id, 'width')}
                      onChange={(e) => commitField(part.id, 'width', e.target.value)}
                    />
                  </td>
                  <td className="cell-small">
                    <input
                      type="text"
                      inputMode={coarse ? 'none' : 'numeric'}
                      className={`cell-input kb-input${isCellActive(part.id, 'qty') ? ' cell-active' : ''}`}
                      aria-label={`Количество ${n}`}
                      data-row-id={part.id}
                      data-field="qty"
                      value={part.qty > 0 ? part.qty : ''}
                      onFocus={() => focusCell(part.id, 'qty')}
                      onKeyDown={(e) => handleCellKeyDown(e, part.id, 'qty')}
                      onBlur={() => normalizeQty(part.id)}
                      onChange={(e) => commitField(part.id, 'qty', e.target.value)}
                    />
                  </td>
                  <td
                    className="cell-edge"
                    aria-label={`Кромки ${n}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setEdgeModal({ id: ensureRow(part.id), label: n })}
                  >
                    <EdgeMiniSvg edges={part.edges ?? EMPTY_EDGES} />
                  </td>
                  <td className="cell-check">
                    <input
                      type="checkbox"
                      className="row-select"
                      aria-label={`Выбрать строку ${n}`}
                      checked={checked.has(part.id)}
                      onChange={(e) => toggleRow(part.id, e.target.checked)}
                    />
                  </td>
                  <td className="cell-text">
                    <div className="autocomplete-wrapper">
                      <input
                        type="text"
                        className="cell-input part-name-input"
                        aria-label={`Наименование ${n}`}
                        placeholder="название детали"
                        value={part.name}
                        onChange={(e) => updatePart(ensureRow(part.id), { name: e.target.value })}
                      />
                    </div>
                  </td>
                  <td className="cell-icon">
                    <button
                      type="button"
                      className={`cell-rotate-toggle ${rotateYes ? 'yes' : 'no'}`}
                      aria-pressed={rotateYes}
                      aria-label={`Можно ли поворачивать деталь ${n}`}
                      onClick={() =>
                        updatePart(ensureRow(part.id), { rotation: rotateYes ? 'forbid' : 'allow' })
                      }
                    >
                      {rotateYes ? 'Да' : 'Нет'}
                    </button>
                  </td>
                  <td
                    className="cell-groove"
                    aria-label={`Пазы ${n}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setGrooveModal({ id: ensureRow(part.id), label: n })}
                  >
                    <GrooveMiniSvg grooves={part.grooves ?? EMPTY_GROOVES} />
                  </td>
                  <td
                    className="cell-process"
                    aria-label={`Обработка ${n}`}
                    role="button"
                    tabIndex={0}
                    onClick={() => setProcessModal({ id: ensureRow(part.id), label: n })}
                  >
                    <span className={`process-chip${part.processing ? ' has-value' : ''}`}>
                      {part.processing || 'Нет'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Футер = инфо-панель сводок ДЕТАЛЕЙ (вербатим project.html:365-370, CUT-3.2,
          PR #12): Деталей · Площадь · Кромка 1 · Кромка 2 · Пазы. Округление —
          площадь 2 знака; кромка 1/2 и пазы 1 знак (решение владельца 11.06.2026).
          КИМ/отходы/листы — это EP-4/EP-5 на экране «Результат», НЕ здесь. */}
      <div className="sc-table-footer">
        <span data-testid="parts-total">
          Деталей: <strong>{countParts(parts)}</strong>
        </span>
        <span data-testid="parts-area">
          Площадь: <strong>{summary.area.toFixed(2)} м.кв.</strong>
        </span>
        <span data-testid="parts-edge1">
          Кромка 1: <strong>{summary.edge1.toFixed(1)} м</strong>
        </span>
        <span data-testid="parts-edge2">
          Кромка 2: <strong>{summary.edge2.toFixed(1)} м</strong>
        </span>
        <span data-testid="parts-grooves">
          Пазы: <strong>{summary.grooves.toFixed(1)} м</strong>
        </span>
      </div>

      {/* Нижние действия (project.html:373-376, вербатим): обе кнопки.
          «Раскроить» блокируется при детали > листа (§5.1.10); клик запускает
          движок раскроя клиентски (CUT-5.1, ADR-0079) → вкладка «Результат». */}
      {hasInvalid && (
        <p className="sc-invalid-message" role="alert">
          ⚠ Недопустимый размер — исправьте красные строки
        </p>
      )}
      <div className="details-bottom-actions">
        <button
          type="button"
          className="btn btn-success btn-lg"
          onClick={() => void autosaveDraft()}
        >
          💾 Сохранить как черновик
        </button>
        <button
          type="button"
          className="btn btn-primary btn-lg"
          disabled={hasInvalid}
          title={hasInvalid ? 'Недопустимый размер' : undefined}
          onClick={() => {
            runCutting();
            onCutComplete?.();
          }}
        >
          ✂ Раскроить
        </button>
      </div>

      {dialog === 'import' && <ImportFormatDialog onClose={() => setDialog(null)} />}
      {dialog === 'clear' && (
        <CuttingModal2
          title="Очистить все детали?"
          footer={
            <>
              <button type="button" className="sc-modal-btn" onClick={() => setDialog(null)}>
                Отмена
              </button>
              <button
                type="button"
                className="sc-modal-btn primary"
                onClick={() => {
                  clearParts();
                  setSelectedId(null);
                  setChecked(new Set());
                  setDialog(null);
                }}
              >
                Удалить все
              </button>
            </>
          }
        >
          <p>Все детали будут удалены. Действие необратимо.</p>
        </CuttingModal2>
      )}

      {edgeModal && (
        <EdgeModal
          partId={edgeModal.id}
          partLabel={edgeModal.label}
          onClose={() => setEdgeModal(null)}
        />
      )}

      {grooveModal && (
        <GrooveModal
          partId={grooveModal.id}
          partLabel={grooveModal.label}
          onClose={() => setGrooveModal(null)}
        />
      )}

      {processModal && (
        <ProcessModal
          partId={processModal.id}
          partLabel={processModal.label}
          onClose={() => setProcessModal(null)}
        />
      )}

      {/* CUT-2.4: цифровая клавиатура — ТОЛЬКО на touch (coarse pointer). Десктоп
          (fine pointer) использует физическую клавиатуру (PR #8b итерация 3,
          #7-FIX скорректирован, решение владельца 10.06.2026). */}
      {coarse && activeCell && (
        <NumericKeyboard
          visible
          value={fieldString(activeCell.id, activeCell.field)}
          onChange={(v) => commitField(activeCell.id, activeCell.field, v)}
          onNavigate={handleKbNavigate}
          onClose={() => {
            if (activeCell.field === 'qty') normalizeQty(activeCell.id);
            setActiveCell(null);
          }}
          allowDecimal
          allowFraction={false}
        />
      )}
    </div>
  );
}
